import { TLastActiveContactProfilingRequest } from 'dialer/classes/models/IChangeContactRequest'
import { TLastActivePartnerProfilingRequest } from 'dialer/classes/models/IChangePartnerRequest'
import { EProfilingRequestSource } from 'dialer/classes/models/Profiling'
import { getTokens } from 'src/core/classes/helpers/StorageHelper'
import { EWSEvent, IWSClient } from 'src/core/classes/ws/IWSClient'
import { TResponse } from 'src/core/classes/ws/WebSocketClient'
import { wsConnectionPool } from 'src/core/classes/ws/WSConnectionPool'

const PROFILING_REQUEST_SERVICE = 'profiling-request'

class OnboardingAPI {
  private readonly serviceName = 'onboarding'
  public async addListeners() {
    wsConnectionPool.addListener(this.serviceName, EWSEvent.open, async (client) => {
      const [accessToken] = getTokens()
      if (accessToken) {
        await onboardingAPI.authorizeByToken(client, accessToken)
      }
    })
  }

  public authorizeByToken = async (client: IWSClient, token: string) => {
    if (token) {
      const response = await client.call('auth', 'token', { token }, true)
      if (response.status === 'success') {
        client.isAuthorized.resolve()
      } else {
        throw new Error('Was not able to authorize')
      }
    }
  }

  public createContactProfilingRequest = async (
    contactId: string,
    updatedFields: Record<string, any>
  ): Promise<{
    success: boolean
  }> => {
    const wsClient = await wsConnectionPool.getAuthorizedClient(this.serviceName)
    try {
      const response = await wsClient.call<TResponse<{ message?: string }>>(
        PROFILING_REQUEST_SERVICE,
        'contact-request',
        {
          source: EProfilingRequestSource.dialer,
          contactId,
          updatedFields,
        }
      )

      return { success: response.status === 'success' }
    } catch (e) {
      return { success: false }
    }
  }

  public createPartnerProfilingRequest = async (
    partnerId: string,
    updatedFields: Record<string, any>
  ): Promise<{
    success: boolean
  }> => {
    const wsClient = await wsConnectionPool.getAuthorizedClient(this.serviceName)
    try {
      const response = await wsClient.call<TResponse<{ message?: string }>>(
        PROFILING_REQUEST_SERVICE,
        'partner-request',
        {
          source: EProfilingRequestSource.dialer,
          partnerId,
          updatedFields,
        }
      )

      return { success: response.status === 'success' }
    } catch (e) {
      return { success: false }
    }
  }

  public async getLastActiveContactProfilingRequestByUpdatedFields(
    contactId: string,
    updatedFields: string[]
  ) {
    const wsClient = await wsConnectionPool.getAuthorizedClient(this.serviceName)
    try {
      const response = await wsClient.call<
        TResponse<{ profilingRequest: TLastActiveContactProfilingRequest | null }>
      >(PROFILING_REQUEST_SERVICE, 'get-last-active-contact-request-by-updated-fields', {
        contactId,
        updatedFields,
      })

      return { success: response.status === 'success', data: response.data.profilingRequest }
    } catch (e) {
      return { success: false, data: null }
    }
  }

  public async getLastActivePartnerProfilingRequestByUpdatedFields(
    partnerId: string,
    updatedFields: string[]
  ) {
    const wsClient = await wsConnectionPool.getAuthorizedClient(this.serviceName)
    try {
      const response = await wsClient.call<
        TResponse<{ profilingRequest: TLastActivePartnerProfilingRequest | null }>
      >(PROFILING_REQUEST_SERVICE, 'get-last-active-partner-request-by-updated-fields', {
        partnerId,
        updatedFields,
      })

      return { success: response.status === 'success', data: response.data.profilingRequest }
    } catch (e) {
      return { success: false, data: null }
    }
  }
}

export const onboardingAPI = new OnboardingAPI()
